/**
 * importJobStore — Zustand store for live import job snapshots (SDD-001 Phase 4b).
 *
 * Keyed by jobId. Each apply* action updates the snapshot immutably.
 * The store is the single source of truth for all WS import_job_* events.
 * ImportProgressCard subscribes to this store directly — no prop drilling.
 */
import { create } from 'zustand'
import type {
  ImportJobSnapshot,
  ImportJobState,
  ImportJobEnqueuedEvent,
  ImportJobStartedEvent,
  ImportJobProgressEvent,
  ImportJobCompletedEvent,
  ImportJobFailedEvent,
  ImportJobStatusResponse,
} from '../types/rich-content'

interface ImportJobStore {
  /** All job snapshots keyed by jobId. */
  jobs: Record<string, ImportJobSnapshot>

  /**
   * Card-204 — fired when confirm_import_proposal hands the job to the queue.
   * Creates the snapshot in `enqueued` so the chat card transitions out of
   * "Confirmar?" before the worker picks the job up.
   */
  applyEnqueued: (event: ImportJobEnqueuedEvent) => void

  /** Called when the engine emits import_job_started. Creates a new snapshot. */
  applyStarted: (event: ImportJobStartedEvent) => void

  /** Called when the engine emits import_job_progress. Updates in-progress fields. */
  applyProgress: (event: ImportJobProgressEvent) => void

  /** Called when the engine emits import_job_completed. Finalises the snapshot. */
  applyCompleted: (event: ImportJobCompletedEvent) => void

  /** Called when the engine emits import_job_failed. Marks snapshot as failed. */
  applyFailed: (event: ImportJobFailedEvent) => void

  /** Aplica o estado autoritativo vindo do REST (watchdog reconciliation). */
  applyReconciled: (status: ImportJobStatusResponse) => void

  /** Marca o job como `stalled` quando o watchdog estoura o teto sem eventos. */
  applyStalled: (jobId: string) => void

  /**
   * Terminal "job não existe mais" — o engine respondeu 404 ao status poll
   * (ex.: a base foi resetada e o IMPORT_JOB deletado). Diferente de um erro de
   * rede transitório (que NÃO remove o card): um 404 é definitivo, então o job
   * é REMOVIDO do estado para o watchdog parar de pollar e o card sumir.
   * A store de snapshots é só em memória; o vetor que faz o card ressurgir em
   * toda conversa nova é a mensagem `import_job_summary` persistida no chatStore,
   * purgada separadamente pelo watchdog via `chatStore.removeMessage`.
   */
  applyNotFound: (jobId: string) => void

  /** Read a single snapshot by jobId. Returns undefined when not found. */
  getJob: (jobId: string) => ImportJobSnapshot | undefined

  /** Remove a snapshot from the store (post-cleanup). */
  clear: (jobId: string) => void
}

export const useImportJobStore = create<ImportJobStore>((set, get) => ({
  jobs: {},

  applyEnqueued: (event: ImportJobEnqueuedEvent) => {
    // Idempotent — if a started/progress event raced ahead of the enqueue
    // ack (rare but possible on reconnect / replay), preserve the more
    // advanced snapshot rather than rewinding to `enqueued`.
    set((state) => {
      const existing = state.jobs[event.jobId]
      if (existing && existing.state !== 'pending') {
        return state
      }
      const snapshot: ImportJobSnapshot = {
        jobId: event.jobId,
        proposalId: event.proposalId,
        state: 'enqueued' as ImportJobState,
        totalRows: event.totalRows,
        imported: 0,
        skipped: 0,
        errors: 0,
        percent: 0,
        startedAt: event.enqueuedAt,
        lastEventAt: Date.now(),
        // Stamp the originating conversationId so the supervisor can emit the
        // terminal summary into the correct thread even when the user navigates.
        ...(event.conversationId !== undefined ? { conversationId: event.conversationId } : {}),
      }
      return { jobs: { ...state.jobs, [event.jobId]: snapshot } }
    })
  },

  applyStarted: (event: ImportJobStartedEvent) => {
    const snapshot: ImportJobSnapshot = {
      jobId: event.jobId,
      proposalId: event.proposalId,
      state: 'running' as ImportJobState,
      totalRows: event.totalRows,
      imported: 0,
      skipped: 0,
      errors: 0,
      percent: 0,
      startedAt: event.startedAt,
      lastEventAt: Date.now(),
    }
    set((state) => ({
      jobs: { ...state.jobs, [event.jobId]: snapshot },
    }))
  },

  applyProgress: (event: ImportJobProgressEvent) => {
    // Upsert: if import_job_started was missed (e.g. card rendered after the
    // event fired, or subscription joined mid-job), synthesize a snapshot from
    // the progress event rather than dropping it silently. Previously
    // `applyProgress` bailed when no existing entry — cards stayed forever in
    // "enqueued" pending state.
    set((state) => {
      const existing = state.jobs[event.jobId]
      const updated: ImportJobSnapshot = {
        ...(existing ?? {
          jobId: event.jobId,
          state: 'running' as ImportJobState,
          totalRows: event.totalRows,
          imported: 0,
          skipped: 0,
          errors: 0,
          percent: 0,
        }),
        state: 'running' as ImportJobState,
        imported: event.imported,
        skipped: event.skipped,
        errors: event.errors,
        totalRows: event.totalRows,
        percent: event.percent,
        currentSheet: event.currentSheet,
        sheetsProcessed: event.sheetsProcessed,
        totalSheets: event.totalSheets,
        lastEventAt: Date.now(),
      }
      return { jobs: { ...state.jobs, [event.jobId]: updated } }
    })
  },

  applyCompleted: (event: ImportJobCompletedEvent) => {
    // Upsert — same reasoning as applyProgress: the terminal state must land
    // even when the started event was missed, otherwise the card is stuck.
    set((state) => {
      const existing = state.jobs[event.jobId]
      const updated: ImportJobSnapshot = {
        ...(existing ?? {
          jobId: event.jobId,
          state: 'completed' as ImportJobState,
          totalRows: event.totalRows,
          imported: 0,
          skipped: 0,
          errors: 0,
          percent: 100,
        }),
        // Fase 0 — o event já carrega o estado durável real ('completed' ou
        // 'completed_with_errors'). Fallback a 'completed' cobre payload de
        // engine anterior à Fase 0 (campo ausente).
        state: event.state ?? ('completed' as ImportJobState),
        imported: event.imported,
        skipped: event.skipped,
        errors: event.errors,
        totalRows: event.totalRows,
        failedRowCount: event.failedRowCount,
        durationMs: event.durationMs,
        percent: 100,
        completedAt: Date.now(),
        lastEventAt: Date.now(),
      }
      return { jobs: { ...state.jobs, [event.jobId]: updated } }
    })
  },

  applyFailed: (event: ImportJobFailedEvent) => {
    set((state) => {
      const existing = state.jobs[event.jobId]
      const updated: ImportJobSnapshot = {
        ...(existing ?? {
          jobId: event.jobId,
          state: 'failed' as ImportJobState,
          totalRows: 0,
          imported: event.imported,
          skipped: 0,
          errors: event.errors,
          percent: 0,
        }),
        state: 'failed' as ImportJobState,
        imported: event.imported,
        errors: event.errors,
        errorMessage: event.errorMessage,
        durationMs: event.durationMs,
        completedAt: Date.now(),
        lastEventAt: Date.now(),
      }
      return { jobs: { ...state.jobs, [event.jobId]: updated } }
    })
  },

  applyReconciled: (status: ImportJobStatusResponse) => {
    set((state) => {
      const existing = state.jobs[status.jobId]
      const updated: ImportJobSnapshot = {
        ...(existing ?? {
          jobId: status.jobId,
          proposalId: status.proposalId,
          state: 'running' as ImportJobState,
          totalRows: 0,
          imported: 0,
          skipped: 0,
          errors: 0,
          percent: 0,
        }),
        proposalId: status.proposalId,
        state: status.state,
        totalRows: status.totalRows,
        imported: status.imported,
        skipped: status.skipped,
        errors: status.errors,
        percent: status.percent,
        failedRowCount: status.failedRowCount ?? existing?.failedRowCount,
        skippedSheets: status.skippedSheets ?? existing?.skippedSheets,
        errorMessage: status.errorMessage ?? existing?.errorMessage,
        startedAt: status.startedAt ?? existing?.startedAt,
        completedAt: status.completedAt ?? existing?.completedAt,
        durationMs: status.durationMs ?? existing?.durationMs,
        lastEventAt: Date.now(),
      }
      return { jobs: { ...state.jobs, [status.jobId]: updated } }
    })
  },

  applyStalled: (jobId: string) => {
    set((state) => {
      const existing = state.jobs[jobId]
      // Só estados não-terminais podem virar stalled — nunca regride um
      // completed/completed_with_errors/failed/canceled que já chegou.
      if (
        !existing ||
        existing.state === 'completed' ||
        existing.state === 'completed_with_errors' ||
        existing.state === 'failed' ||
        existing.state === 'canceled'
      ) {
        return state
      }
      return {
        jobs: {
          ...state.jobs,
          [jobId]: {
            ...existing,
            state: 'stalled' as ImportJobState,
            completedAt: Date.now(),
            lastEventAt: Date.now(),
          },
        },
      }
    })
  },

  applyNotFound: (jobId: string) => {
    // 404 is terminal-and-gone: drop the snapshot entirely so the card stops
    // rendering and the watchdog short-circuits (its TERMINAL/absent guards).
    // Idempotent — a no-op when the job was already removed.
    set((state) => {
      if (!(jobId in state.jobs)) return state
      const next = { ...state.jobs }
      delete next[jobId]
      return { jobs: next }
    })
  },

  getJob: (jobId: string) => get().jobs[jobId],

  clear: (jobId: string) => {
    set((state) => {
      const next = { ...state.jobs }
      delete next[jobId]
      return { jobs: next }
    })
  },
}))
